<template>
  <div class="dashboard-container container">
    <div class="page-header">
      <h1>Dashboard</h1>
      <p>Bienvenido al panel de control, {{ currentUser.full_name || currentUser.email }}</p>
    </div>
    
    <div class="stats-cards">
      <Card v-if="isAdmin"  class="stats-card">
        <template #title>
          <div class="card-title">
            <i class="pi pi-users"></i>
            <span>Usuarios</span>
          </div>
        </template>
        <template #content>
          <div class="card-content">
            <div class="stats-value">{{ users.length }}</div>
            <div class="stats-label">Usuarios registrados</div>
            <div class="stats-trend positive">
              <i class="pi pi-arrow-up"></i>
              <span>5% desde el mes pasado</span>
            </div>
          </div>
          <div class="card-footer">
            <Button label="Gestionar Usuarios" icon="pi pi-user-edit" class="p-button-primary" @click="goToUserManagement" v-if="isAdmin" />
          </div>
        </template>
      </Card>
      
      <Card class="stats-card">
        <template #title>
          <div class="card-title">
            <i class="pi pi-file"></i>
            <span>Archivos</span>
          </div>
        </template>
        <template #content>
          <div class="card-content">
            <div class="stats-value">{{ uploadedFiles.length }}</div>
            <div class="stats-label">Archivos subidos</div>
            <div class="stats-trend positive">
              <i class="pi pi-arrow-up"></i>
              <span>12% desde el mes pasado</span>
            </div>
          </div>
          <div class="card-footer">
            <Button label="Subir Archivos" icon="pi pi-upload" class="p-button-primary" @click="goToUpload" />
          </div>
        </template>
      </Card>
      
      <Card class="stats-card">
        <template #title>
          <div class="card-title">
            <i class="pi pi-database"></i>
            <span>Almacenamiento</span>
          </div>
        </template>
        <template #content>
          <div class="card-content">
            <div class="stats-value">{{ calculateStorage }}</div>
            <div class="stats-label">Espacio total utilizado</div>
            <div class="progress-container">
              <div class="progress-bar" :style="{ width: '100%' }"></div>
            </div>
            <div class="progress-label">{{ uploadedFiles.length }} archivos almacenados</div>
          </div>
          <div class="card-footer">
            <Button label="Ver Detalles" icon="pi pi-chart-bar" class="p-button-primary" @click="goToStorage" />
          </div>
        </template>
      </Card>
    </div>
    
    <div class="dashboard-row">
      <Card class="recent-files-card">
        <template #title>
          <div class="section-header">
            <h2><i class="pi pi-list"></i> Archivos Recientes</h2>
            <div class="section-actions">
              <Button label="Ver Todos" icon="pi pi-external-link" class="p-button-text" @click="goToUpload" />
            </div>
          </div>
        </template>
        <template #content>
          <DataTable 
            :value="uploadedFiles.slice(0, 5)" 
            :loading="loading"
            class="files-table"
            stripedRows
            responsiveLayout="scroll"
            :paginator="false"
          >
            <Column field="original_filename" header="Nombre Original">
              <template #body="slotProps">
                <div class="file-name">
                  <i :class="getFileIcon(slotProps.data.file_type)"></i>
                  <span>{{ slotProps.data.original_filename }}</span>
                </div>
              </template>
            </Column>
            <Column field="patient_id" header="ID Paciente">
              <template #body="slotProps">
                <span>{{ extractPatientId(slotProps.data.s3_path) }}</span>
              </template>
            </Column>
            <Column field="s3_path" header="Ruta S3">
              <template #body="slotProps">
                <span class="text-overflow">{{ formatS3Path(slotProps.data.s3_path) }}</span>
              </template>
            </Column>
            <Column field="file_type" header="Tipo"></Column>
            <Column field="size_bytes" header="Tamaño">
              <template #body="slotProps">
                <span>{{ formatFileSize(slotProps.data.size_bytes) }}</span>
              </template>
            </Column>
            <Column field="created_at" header="Fecha de Subida">
              <template #body="slotProps">
                <span>{{ formatDate(slotProps.data.created_at) }}</span>
              </template>
            </Column>
          </DataTable>
          <div v-if="uploadedFiles.length === 0 && !loading" class="empty-state">
            <i class="pi pi-inbox"></i>
            <p>No hay archivos subidos todavía</p>
            <Button label="Subir ahora" icon="pi pi-upload" class="p-button-primary" @click="goToUpload" />
          </div>
        </template>
      </Card>
      
      <Card v-if="isAdmin" class="activity-card">
        <template #title>
          <div class="section-header">
            <h2><i class="pi pi-history"></i> Actividad Reciente</h2>
            <Button 
              icon="pi pi-refresh" 
              class="p-button-text p-button-rounded" 
              @click="fetchRecentActivity" 
              :loading="loadingActivity"
            />
          </div>
        </template>
        <template #content>
          <div v-if="recentActivity.length > 0" class="activity-timeline">
            <div v-for="(activity, index) in recentActivity" :key="index" class="timeline-item">
              <div :class="['timeline-icon', activity.type]">
                <i :class="getActivityIcon(activity.type)"></i>
              </div>
              <div class="timeline-content">
                <div class="timeline-header">
                  <span class="timeline-title">{{ activity.title }}</span>
                  <span class="timeline-time">{{ formatTimeAgo(activity.timestamp) }}</span>
                </div>
                <p>{{ activity.description }}</p>
              </div>
            </div>
          </div>
          
          <div v-else class="empty-state">
            <i class="pi pi-inbox"></i>
            <p>No hay actividad reciente</p>
            <p class="empty-state-hint">Las acciones realizadas en el sistema aparecerán aquí.</p>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  name: 'DashboardView',
  data() {
    return {
      storagePercentage: 35,
      recentActivity: [],
      loadingActivity: false
    }
  },
  computed: {
    ...mapState('auth', ['currentUser']),
    ...mapState('files', ['uploadedFiles', 'loading']),
    ...mapState('users', ['users']),
    ...mapGetters('auth', ['isAdmin']),
    
    calculateStorage() {
      // Calcular el tamaño total de los archivos
      let totalSize = 0;
      this.uploadedFiles.forEach(file => {
        if (file.size_bytes) {
          totalSize += parseInt(file.size_bytes);
        }
      });
      
      return this.formatFileSize(totalSize);
    }
  },
  methods: {
    ...mapActions('files', ['fetchMyUploadedFiles']),
    ...mapActions('users', ['fetchUsers']),
    
    goToUserManagement() {
      this.$router.push({ name: 'users' })
    },
    
    goToUpload() {
      this.$router.push({ name: 'upload' })
    },
    
    goToStorage() {
      // Esta función podría implementarse en el futuro
      // para mostrar detalles de almacenamiento
      this.$router.push({ name: 'upload' })
    },
    
    formatS3Path(path) {
      return path && path.length > 30 ? path.substring(0, 30) + '...' : path
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      
      const date = new Date(dateString)
      return date.toLocaleString()
    },
    
    formatFileSize(sizeInBytes) {
      if (!sizeInBytes) return '0 B'
      
      const bytes = parseInt(sizeInBytes)
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      
      if (bytes === 0) return '0 B'
      
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)))
      
      return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i]
    },
    
    getFileIcon(fileType) {
      if (!fileType) return 'pi pi-file';
      
      if (fileType.includes('zip') || fileType.includes('archive')) return 'pi pi-file-excel';
      if (fileType.includes('fastq') || fileType.includes('bam') || fileType.includes('vcf')) return 'pi pi-file-excel';
      if (fileType.includes('csv')) return 'pi pi-file-excel';
      if (fileType.includes('txt')) return 'pi pi-file-o';
      
      return 'pi pi-file';
    },
    
    async fetchRecentActivity() {
      this.loadingActivity = true;
      try {
        // Aquí se implementará la llamada a la API para obtener la actividad reciente
        // Por ahora dejamos el array vacío
        this.recentActivity = [];
      } catch (error) {
        console.error('Error al cargar la actividad reciente:', error);
      } finally {
        this.loadingActivity = false;
      }
    },
    
    getActivityIcon(type) {
      const icons = {
        upload: 'pi pi-upload',
        user: 'pi pi-user',
        data: 'pi pi-database',
        default: 'pi pi-info-circle'
      };
      return icons[type] || icons.default;
    },
    
    formatTimeAgo(timestamp) {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) return 'hace unos segundos';
      if (diffInSeconds < 3600) return `hace ${Math.floor(diffInSeconds / 60)} minutos`;
      if (diffInSeconds < 86400) return `hace ${Math.floor(diffInSeconds / 3600)} horas`;
      if (diffInSeconds < 2592000) return `hace ${Math.floor(diffInSeconds / 86400)} días`;
      
      return date.toLocaleDateString();
    },
    
    extractPatientId(s3Path) {
      if (!s3Path) return 'N/A';
      
      // Intenta extraer el ID del formato paciente-XXXXX/
      const patientMatch = s3Path.match(/paciente-(\d+)\//);
      if (patientMatch && patientMatch[1]) {
        return patientMatch[1];
      }
      
      // Si no encuentra el formato anterior, intenta extraer de la primera parte del nombre del archivo
      const parts = s3Path.split('/');
      const fileName = parts[parts.length - 1];
      
      // Busca patrones como 60346-D, 99998R o 60346U-87628-R
      const idMatch = fileName.match(/^(\d+)(?:[A-Za-z]-|[A-Za-z])/);
      if (idMatch && idMatch[1]) {
        return idMatch[1];
      }
      
      return 'N/A';
    }
  },
  created() {
    // Cargar datos necesarios
    this.fetchMyUploadedFiles()
    
    // Si es admin, cargar usuarios
    if (this.isAdmin) {
      this.fetchUsers()
    }
    this.fetchRecentActivity();
  }
}
</script>

<style scoped>
.dashboard-container {
  padding-bottom: 40px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
  margin-bottom: 30px;
}

.stats-card {
  height: 100%;
  transition: transform 0.3s, box-shadow 0.3s;
}

.stats-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--primary-dark);
}

.card-title i {
  font-size: 1.2rem;
}

.card-content {
  padding: 20px 0;
  position: relative;
}

.stats-value {
  font-size: 2.5rem;
  font-weight: 600;
  color: var(--primary-dark);
  margin-bottom: 8px;
  text-align: center;
}

.stats-label {
  font-size: 1rem;
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: 15px;
}

.stats-trend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 0.8rem;
  margin-top: 5px;
}

.stats-trend.positive {
  color: var(--success-color);
}

.stats-trend.negative {
  color: var(--error-color);
}

.progress-container {
  background-color: #e9ecef;
  border-radius: 10px;
  height: 8px;
  overflow: hidden;
  margin: 10px 0;
}

.progress-bar {
  height: 100%;
  background-color: var(--primary-color);
  border-radius: 10px;
  transition: width 0.3s ease;
}

.progress-label {
  text-align: center;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.card-footer {
  display: flex;
  justify-content: center;
  margin-top: 15px;
}

.dashboard-row {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 25px;
  margin-top: 25px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header h2 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.3rem;
  color: var(--primary-dark);
}

.section-header h2 i {
  color: var(--primary-color);
}

.recent-files-card, .activity-card {
  height: 100%;
}

.files-table {
  width: 100%;
}

.file-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-name i {
  color: var(--primary-color);
}

.text-overflow {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  display: inline-block;
}

.view-all-container {
  display: flex;
  justify-content: center;
  margin-top: 15px;
}

.empty-state {
  text-align: center;
  padding: 30px 0;
  color: var(--text-secondary);
}

.empty-state i {
  font-size: 2.5rem;
  margin-bottom: 15px;
  color: #d0d0d0;
}

.empty-state p {
  margin-bottom: 20px;
}

.activity-timeline {
  padding: 10px 0;
}

.timeline-item {
  display: flex;
  gap: 15px;
  padding: 15px 0;
  position: relative;
}

.timeline-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 15px;
  top: 55px;
  bottom: 0;
  width: 2px;
  background-color: #e0e0e0;
}

.timeline-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-light);
  color: white;
  position: relative;
  z-index: 1;
}

.timeline-icon.upload {
  background-color: var(--primary-color);
}

.timeline-icon.user {
  background-color: var(--secondary-color);
}

.timeline-icon.data {
  background-color: var(--accent-color);
}

.timeline-content {
  flex: 1;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.timeline-title {
  font-weight: 600;
  color: var(--text-primary);
}

.timeline-time {
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.timeline-content p {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.empty-state-hint {
  font-size: 0.9rem !important;
  opacity: 0.7;
  margin-top: 5px;
}

@media (max-width: 992px) {
  .dashboard-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
}
</style> 